import { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  QueryStateBoundary,
} from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import {
  useDeleteMenuRow,
  useEventMenu,
  useGenerateMenuPdf,
  usePatchMenuRow,
} from "@/api/hooks/useEventMenu";
import { useGenerateShoppingList, useGenerateShoppingListPdf, useShoppingList } from "@/api/hooks/useShoppingList";
import { menuRowName, menuRowStory, type MenuRow } from "@/api/types/menu";
import { formatOz } from "@/lib/format";
import type { Event } from "@/api/types/event";
import { AddMenuRowDialog } from "./AddMenuRowDialog";

interface MenuShoppingListCardProps {
  event: Event;
}

function sortByOrder(rows: MenuRow[]): MenuRow[] {
  return [...rows].sort((a, b) => a.sort_order - b.sort_order);
}

/**
 * Phase 4's event-facing card: the menu builder (attach library drinks or
 * define event-customs) plus the shopping-list generator and its two client
 * PDFs. Additive to EventDetailPage — does not touch any existing card.
 */
export function MenuShoppingListCard({ event }: MenuShoppingListCardProps) {
  const { data: rows, isLoading, error, refetch } = useEventMenu(event.id);
  const [addOpen, setAddOpen] = useState(false);

  const sortedRows = useMemo(() => sortByOrder(rows ?? []), [rows]);
  const existingDrinkIds = useMemo(
    () => sortedRows.map((r) => r.drink_id).filter((id): id is string => !!id),
    [sortedRows],
  );
  const nextSortOrder = sortedRows.length > 0 ? sortedRows[sortedRows.length - 1].sort_order + 1 : 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Menu &amp; shopping list</CardTitle>
        <Button size="sm" onClick={() => setAddOpen(true)}>
          Add drink
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
          {sortedRows.length === 0 ? (
            <p className="text-sm text-text-muted" data-testid="menu-empty">
              No drinks on this menu yet.
            </p>
          ) : (
            <ul className="divide-y divide-border" data-testid="menu-list">
              {sortedRows.map((row, i) => (
                <MenuRowItem
                  key={row.id}
                  row={row}
                  eventId={event.id}
                  isFirst={i === 0}
                  isLast={i === sortedRows.length - 1}
                  prevId={i > 0 ? sortedRows[i - 1].id : undefined}
                  prevSortOrder={i > 0 ? sortedRows[i - 1].sort_order : undefined}
                  nextId={i < sortedRows.length - 1 ? sortedRows[i + 1].id : undefined}
                  nextSortOrderValue={i < sortedRows.length - 1 ? sortedRows[i + 1].sort_order : undefined}
                />
              ))}
            </ul>
          )}
        </QueryStateBoundary>

        <ShoppingListSection eventId={event.id} hasMenu={sortedRows.length > 0} />
      </CardContent>

      <AddMenuRowDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        eventId={event.id}
        existingDrinkIds={existingDrinkIds}
        nextSortOrder={nextSortOrder}
      />
    </Card>
  );
}

// ---- Menu row -------------------------------------------------------------

interface MenuRowItemProps {
  row: MenuRow;
  eventId: string;
  isFirst: boolean;
  isLast: boolean;
  prevId?: string;
  prevSortOrder?: number;
  nextId?: string;
  nextSortOrderValue?: number;
}

function MenuRowItem({
  row,
  eventId,
  isFirst,
  isLast,
  prevId,
  prevSortOrder,
  nextId,
  nextSortOrderValue,
}: MenuRowItemProps) {
  const patchRow = usePatchMenuRow();
  const deleteRow = useDeleteMenuRow();

  async function toggleNa(checked: boolean) {
    try {
      await patchRow.mutateAsync({ eventId, rowId: row.id, serve_na: checked });
    } catch (err) {
      toastError("Couldn't update NA setting", getErrorMessage(err));
    }
  }

  async function moveUp() {
    if (!prevId || prevSortOrder === undefined) return;
    try {
      await Promise.all([
        patchRow.mutateAsync({ eventId, rowId: row.id, sort_order: prevSortOrder }),
        patchRow.mutateAsync({ eventId, rowId: prevId, sort_order: row.sort_order }),
      ]);
    } catch (err) {
      toastError("Couldn't reorder menu", getErrorMessage(err));
    }
  }

  async function moveDown() {
    if (!nextId || nextSortOrderValue === undefined) return;
    try {
      await Promise.all([
        patchRow.mutateAsync({ eventId, rowId: row.id, sort_order: nextSortOrderValue }),
        patchRow.mutateAsync({ eventId, rowId: nextId, sort_order: row.sort_order }),
      ]);
    } catch (err) {
      toastError("Couldn't reorder menu", getErrorMessage(err));
    }
  }

  async function remove() {
    try {
      await deleteRow.mutateAsync({ eventId, rowId: row.id });
    } catch (err) {
      toastError("Couldn't remove drink", getErrorMessage(err));
    }
  }

  const story = menuRowStory(row);

  return (
    <li className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between" data-menu-row-id={row.id}>
      <div className="min-w-0">
        <p className="font-medium text-text-primary">
          {menuRowName(row)}
          {row.is_custom && (
            <Badge variant="secondary" size="sm" className="ml-2">
              Custom
            </Badge>
          )}
        </p>
        {story && <p className="line-clamp-1 text-xs text-text-muted">{story}</p>}
      </div>

      <div className="flex items-center gap-3">
        <Checkbox
          label="Serve NA"
          checked={row.serve_na}
          disabled={patchRow.isPending}
          onChange={(e) => void toggleNa(e.target.checked)}
        />
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Move ${menuRowName(row)} up`}
            disabled={isFirst || patchRow.isPending}
            onClick={() => void moveUp()}
          >
            ↑
          </Button>
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Move ${menuRowName(row)} down`}
            disabled={isLast || patchRow.isPending}
            onClick={() => void moveDown()}
          >
            ↓
          </Button>
        </div>
        <Button
          variant="ghost-danger"
          size="sm"
          disabled={deleteRow.isPending}
          onClick={() => void remove()}
        >
          Remove
        </Button>
      </div>
    </li>
  );
}

// ---- Shopping list ---------------------------------------------------------

function ShoppingListSection({ eventId, hasMenu }: { eventId: string; hasMenu: boolean }) {
  const { data: shoppingList, isLoading } = useShoppingList(eventId);
  const generate = useGenerateShoppingList();
  const generateListPdf = useGenerateShoppingListPdf();
  const generateMenuPdf = useGenerateMenuPdf();
  const [listPdfHref, setListPdfHref] = useState<string | null>(null);
  const [menuPdfHref, setMenuPdfHref] = useState<string | null>(null);

  async function handleGenerate() {
    try {
      await generate.mutateAsync({ eventId });
      toastSuccess("Shopping list generated");
    } catch (err) {
      toastError("Couldn't generate shopping list", getErrorMessage(err));
    }
  }

  async function handleGenerateListPdf() {
    try {
      const result = await generateListPdf.mutateAsync(eventId);
      // Contract: "return {pdf_key} or a URL". Prefer an explicit url; a
      // bare pdf_key falls back to a GET on the same collection path,
      // mirroring the quotes feature's `/pdf` GET-after-generate convention.
      setListPdfHref(result.url ?? `/api/v2/events/${eventId}/shopping-list/pdf`);
      toastSuccess("Shopping list PDF ready");
    } catch (err) {
      toastError("Couldn't generate shopping-list PDF", getErrorMessage(err));
    }
  }

  async function handleGenerateMenuPdf() {
    try {
      const result = await generateMenuPdf.mutateAsync(eventId);
      setMenuPdfHref(result.url ?? `/api/v2/events/${eventId}/menu/pdf`);
      toastSuccess("Menu / story cards PDF ready");
    } catch (err) {
      toastError("Couldn't generate menu PDF", getErrorMessage(err));
    }
  }

  return (
    <div className="border-t border-border pt-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-text-primary">Shopping list</h3>
        <Button
          size="sm"
          disabled={!hasMenu || generate.isPending}
          onClick={() => void handleGenerate()}
        >
          {generate.isPending ? "Generating..." : "Generate shopping list"}
        </Button>
      </div>

      {!hasMenu && (
        <p className="text-sm text-text-muted">Add at least one menu drink before generating.</p>
      )}

      {isLoading && hasMenu && <p className="text-sm text-text-muted">Loading...</p>}

      {shoppingList && (
        <div className="space-y-4" data-testid="shopping-list-result">
          <p className="text-xs text-text-muted">
            Basis: {shoppingList.payload.alcohol_guests} alcohol guests
            {shoppingList.payload.na_guests > 0 ? ` + ${shoppingList.payload.na_guests} NA guests` : ""}
            {" · "}
            {shoppingList.payload.drinks_per_guest} drinks/guest
            {" · "}
            {shoppingList.payload.buffer_pct}% buffer
          </p>

          <ShoppingListGroupTable
            title="Spirits"
            testId="spirits"
            lines={shoppingList.payload.groups.spirits}
            countLabel="Bottles (750ml)"
          />
          <ShoppingListGroupTable
            title="Mixers"
            testId="mixers"
            lines={shoppingList.payload.groups.mixers}
            countLabel="Units"
          />
          <ShoppingListGroupTable
            title="Garnish"
            testId="garnish"
            lines={shoppingList.payload.groups.garnish}
            countLabel="Units"
          />

          <div className="flex flex-wrap gap-4 pt-2">
            <div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void handleGenerateListPdf()}
                disabled={generateListPdf.isPending}
              >
                {generateListPdf.isPending ? "Preparing..." : "Download shopping list PDF"}
              </Button>
              {listPdfHref && (
                <a
                  href={listPdfHref}
                  target="_blank"
                  rel="noreferrer"
                  className="ml-2 text-sm font-medium text-primary hover:underline"
                  data-testid="shopping-list-pdf-link"
                >
                  Open PDF
                </a>
              )}
            </div>
            <div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void handleGenerateMenuPdf()}
                disabled={generateMenuPdf.isPending}
              >
                {generateMenuPdf.isPending ? "Preparing..." : "Download menu / story cards PDF"}
              </Button>
              {menuPdfHref && (
                <a
                  href={menuPdfHref}
                  target="_blank"
                  rel="noreferrer"
                  className="ml-2 text-sm font-medium text-primary hover:underline"
                  data-testid="menu-pdf-link"
                >
                  Open PDF
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface ShoppingListGroupTableProps {
  title: string;
  testId: string;
  lines: { ingredient: string; total_oz: number; bottles_750ml: number }[];
  countLabel: string;
}

function ShoppingListGroupTable({ title, testId, lines, countLabel }: ShoppingListGroupTableProps) {
  return (
    <div data-testid={`shopping-list-group-${testId}`}>
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-text-muted">{title}</p>
      {lines.length === 0 ? (
        <p className="text-xs text-text-muted">None on this menu.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-left text-sm">
            <thead className="bg-bg-hover/50 text-xs uppercase tracking-wide text-text-muted">
              <tr>
                <th className="px-3 py-1.5 font-medium">Ingredient</th>
                <th className="px-3 py-1.5 font-medium">Total oz</th>
                <th className="px-3 py-1.5 font-medium">{countLabel}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {lines.map((line) => (
                <tr key={line.ingredient}>
                  <td className="px-3 py-1.5 text-text-primary">{line.ingredient}</td>
                  <td className="px-3 py-1.5 text-text-secondary">{formatOz(line.total_oz)}</td>
                  <td className="px-3 py-1.5 font-medium text-text-primary">{line.bottles_750ml}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
